import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Select from "react-select";
import { IoCloseOutline } from "react-icons/io5";
import API_URL from "../../src/Config";
import "../forms/voyageForm.css";
import {
  EXCAVATION_PERMIT_LABEL,
  buildApprovalPayload,
  buildDepartmentOptions,
  emptyApprovalRow,
  findExcavationPermitType,
  validateApprovalRows,
} from "./excavationApprovalSetupUtils";

function AddExcavationDepartmentApprovalSetup({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [permitOption, setPermitOption] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [rows, setRows] = useState([emptyApprovalRow()]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchFormOptions = useCallback(async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const [permitResponse, departmentResponse] = await Promise.all([
        axios.get(`${API_URL}/api/get_work_permit_types`, {
          headers: { Authorization: authKey },
        }),
        axios.get(`${API_URL}/api/get_departments`, {
          headers: { Authorization: authKey },
        }),
      ]);

      const excavationPermit = findExcavationPermitType(
        permitResponse.data?.work_permit_types || []
      );

      if (excavationPermit) {
        setPermitOption({
          value: excavationPermit.id,
          label: EXCAVATION_PERMIT_LABEL,
        });

        const existingSetupResponse = await axios.get(
          `${API_URL}/api/permit/departments/setup/${excavationPermit.id}`,
          { headers: { Authorization: authKey } }
        );

        if ((existingSetupResponse.data?.departments || []).length > 0) {
          navigate(
            `/edit-excavation-department-approval-setup/${excavationPermit.id}`
          );
          return;
        }
      }

      setDepartmentOptions(buildDepartmentOptions(departmentResponse.data || []));
    } catch (error) {
      console.error("Error fetching excavation setup options:", error.message);
      setSeverity("error");
      showSnackbar("Unable to fetch permit setup options.");
    }
  }, [navigate, showSnackbar]);

  useEffect(() => {
    fetchFormOptions();
  }, [fetchFormOptions]);

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${field}_${index}`]: "",
    }));
  };

  const handleDepartmentChange = (index, selectedOption) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              department_id: selectedOption ? selectedOption.value : "",
              department_name: selectedOption ? selectedOption.label : "",
            }
          : row
      )
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`department_id_${index}`]: "",
    }));
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, emptyApprovalRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prevRows) =>
      prevRows.length > 1
        ? prevRows.filter((_, rowIndex) => rowIndex !== index)
        : prevRows
    );
  };

  const handleCancelClick = () => {
    navigate("/excavation-department-approval-setup");
  };

  const handleSaveClick = async () => {
    if (!permitOption?.value) {
      setSeverity("error");
      showSnackbar("Excavation permit type was not found.");
      return;
    }

    const validationErrors = validateApprovalRows(rows);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setSeverity("error");
      showSnackbar("Please fix the highlighted fields.");
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildApprovalPayload(permitOption.value, rows);

      const response = await axios.post(
        `${API_URL}/api/permit/departments/setup`,
        payload,
        { headers: { Authorization: authKey } }
      );

      if (response.status === 201) {
        setSeverity("success");
        showSnackbar("Department approval setup added successfully");
        setTimeout(() => navigate("/excavation-department-approval-setup"), 1000);
      }
    } catch (error) {
      console.error("Error saving excavation setup:", error.message);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while saving department approvals."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New Excavation Department Approval Setup</PageBackTitle>
              <div className="data-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail" onKeyDown={handleKeyDown}>
              <div className="new-data-form">
                <div className="form-group-select">
                  <label>
                    Permit Type<span className="text-danger">*</span>
                  </label>
                  <Select
                    options={permitOption ? [permitOption] : []}
                    value={permitOption}
                    isDisabled
                    placeholder="Excavation"
                  />
                </div>

                {rows.map((row, index) => (
                  <React.Fragment key={`approval-row-${index}`}>
                    <div className="form-group-select">
                      <label>
                        Department {index + 1}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={departmentOptions}
                        value={
                          departmentOptions.find(
                            (option) => option.value === row.department_id
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleDepartmentChange(index, selectedOption)
                        }
                        placeholder="Select department"
                      />
                      {errors[`department_id_${index}`] && (
                        <div className="error-message">
                          {errors[`department_id_${index}`]}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Approval Order {index + 1}
                        <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          min="1"
                          value={row.approval_order}
                          onChange={(event) =>
                            handleRowChange(
                              index,
                              "approval_order",
                              event.target.value
                            )
                          }
                          placeholder="Enter approval order"
                        />
                        {errors[`approval_order_${index}`] && (
                          <div className="error-message">
                            {errors[`approval_order_${index}`]}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="checkbox-form-group">
                      <label className="checkbox-form-group-label">
                        Approval Type {index + 1}
                      </label>
                      <div className="fuel-checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={row.is_required === "1"}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "is_required",
                                event.target.checked ? "1" : "0"
                              )
                            }
                            className="voyage-checkbox"
                          />
                          Required
                        </label>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={row.is_parallel === "1"}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "is_parallel",
                                event.target.checked ? "1" : "0"
                              )
                            }
                            className="voyage-checkbox"
                          />
                          Parallel
                        </label>
                      </div>
                      {errors[`is_parallel_${index}`] && (
                        <div className="error-message">
                          {errors[`is_parallel_${index}`]}
                        </div>
                      )}
                    </div>

                    {rows.length > 1 && (
                      <div className="form-group">
                        <label></label>
                        <div className="input-group">
                          <button
                            type="button"
                            className="data-form-cancel-button"
                            onClick={() => handleRemoveRow(index)}
                          >
                            <IoCloseOutline /> Remove Department
                          </button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                <div className="form-group">
                  <label></label>
                  <div className="input-group">
                    <button
                      type="button"
                      className="data-form-save-button"
                      onClick={handleAddRow}
                    >
                      Add Department
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default AddExcavationDepartmentApprovalSetup;
